/**
 * 確定履歴一覧取得 hook。
 *
 * ListDemandConfirmRecords BFF RPC を呼んで確定履歴の一覧を取得する。
 * 確定履歴から取込ダイアログ (`ConfirmRecordImportDialog`) 専用の page-local hook。
 */
import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type {
  ConfirmCategory,
  PlanningSection,
} from '@ebalance/proto/bffuser/v1/demand_confirm_record_model_pb'
import { listDemandConfirmRecords } from '@ebalance/proto/bffuser/v1/demand_confirm_record_service-DemandConfirmRecordService_connectquery'
import {
  ListDemandConfirmRecordsRequestSchema,
  type ListDemandConfirmRecordsResponse,
} from '@ebalance/proto/bffuser/v1/demand_confirm_record_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

type ListFilter = {
  planningSectionFilter: PlanningSection
  areaCodeFilter?: AreaCode
  confirmCategoryFilter: ConfirmCategory
  /**
   * 現在の case の BG (useGetWorkflowCase(caseId).bgCode) に絞り込む。未指定
   * (undefined) は絞り込みなし (demand-confirm-record-bg-axis Design Note
   * Decision #4)。他 BG の確定記録を誤って取込候補に含めないための必須フィルタ。
   */
  bgCode?: string
  /**
   * true のときのみ fetch を実行する（既定 true = 既存呼び出し元との後方互換）。
   * ダイアログ表示時のみ取得したい呼び出し元 (確定履歴から取込ダイアログ等) は
   * false を渡し、開いたタイミングで true にする。
   */
  enabled?: boolean
}

/**
 * 取得失敗はダイアログ内に出して再試行させる (issue #12037)。 error boundary
 * (全画面エラー) に飛ばすと編集中の需要想定ごと画面が消えるため、 global
 * QueryClient の `throwOnError: true` を本 query だけ `false` に上書きする
 * (docs/guides/frontend-coding-guide.yaml「queries は throwOnError: true」からの意図的な逸脱。
 * 先例: useSearchSimilarDays / issue #10429)。
 */
export function useListDemandConfirmRecords(
  filter: ListFilter,
): UseQueryResult<ListDemandConfirmRecordsResponse, ConnectError> {
  return useQuery(
    listDemandConfirmRecords,
    create(ListDemandConfirmRecordsRequestSchema, {
      planningSectionFilter: filter.planningSectionFilter,
      areaCodeFilter: filter.areaCodeFilter,
      confirmCategoryFilter: filter.confirmCategoryFilter,
      bgCode: filter.bgCode,
    }),
    {
      enabled: filter.enabled ?? true,
      throwOnError: false,
    },
  )
}
